// CTV-610: in-editor preview surface. Renders the shared ClipPlayer's two
// stacked (double-buffered) <video>s and a play/pause control; playback
// sequencing, the playhead and the hold-to-24s tail live in the player (see
// clipPlayer.ts) so the timeline scrubber and this stay in sync. Shown clean —
// the blur/dim/vignette treatment is baked by the server render.

import React from 'react';
import { Play, Pause } from 'lucide-react';
import type { ClipPlayer } from './clipPlayer';

const TimelinePreview: React.FC<{ player: ClipPlayer }> = ({ player }) => {
  const [playing, setPlaying] = React.useState(player.isPlaying());
  const attach0 = React.useCallback((el: HTMLVideoElement | null) => player.attachVideo(el, 0), [player]);
  const attach1 = React.useCallback((el: HTMLVideoElement | null) => player.attachVideo(el, 1), [player]);

  React.useEffect(() => player.onPlaying(setPlaying), [player]);

  return (
    <div className="space-y-2">
      <div className="relative mx-auto w-full max-w-sm aspect-video rounded-lg overflow-hidden bg-black">
        <video
          ref={attach0}
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 1 }}
        />
        <video
          ref={attach1}
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0 }}
        />
        <button
          type="button"
          onClick={() => player.toggle()}
          className="absolute inset-0 z-20 flex items-center justify-center group"
          aria-label={playing ? 'Pause preview' : 'Play preview'}
        >
          <span
            className={
              'flex items-center justify-center w-12 h-12 rounded-full bg-white/85 text-slate-900 shadow-lg transition-all group-hover:scale-105 ' +
              (playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100')
            }
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </span>
        </button>
      </div>
      <p className="text-center text-[11px] text-slate-400">
        Preview of your stitched composition. Blur, dim and vignette are added by the render on save.
      </p>
    </div>
  );
};

export default TimelinePreview;
